/**
 * PDF Export Button - Main export button with permission checking
 */
import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { canExportPDF, getUserRole } from '../utils/permissions';
import PDFExportModal from './PDFExportModal';

interface PDFExportButtonProps {
  currentUser: any;
  organizationStaff: any[];
  organizationName?: string;
  organizationId?: string;
  className?: string;
}

export default function PDFExportButton({ 
  currentUser, 
  organizationStaff, 
  organizationName = 'Organization',
  organizationId,
  className = '' 
}: PDFExportButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if user has permission to export
  const hasPermission = canExportPDF(currentUser, organizationStaff);
  const userRole = getUserRole(currentUser, organizationStaff);

  // Don't render anything if user doesn't have permission
  if (!hasPermission) {
    return null;
  }

  return (
    <div className={className}>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-green-800 font-medium mb-2">Export All Content to PDF</p>
            <p className="text-green-700 text-sm mb-3">
              Generate a comprehensive PDF document containing all site content with 
              organization branding and hierarchical structure.
            </p>
            <p className="text-green-600 text-xs mb-3">
              Available to: {userRole}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export to PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <PDFExportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        organizationName={organizationName}
        organizationId={organizationId}
        currentUser={currentUser}
      />
    </div>
  );
}
